import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Color } from 'views/constants';
import { Comment } from 'models/Distribution/type';
import { CommentDetail } from 'views/components/organisms/CommentDetail';
import { Driver } from 'views/components/atoms/Driver';
import { HeaderMenu } from 'views/components/atoms/HeaderMenu';
import { Image } from 'views/components/atoms/Image';
import { Typography } from 'views/components/atoms/Typography';
import { CommentModal } from 'views/components/organisms/CommentModal';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useIntl } from 'react-intl';
import { localeIds } from 'locals';

const HeaderMenuContents = styled.div`
  display: flex;
  justify-content: center;
  text-align: left;
  overflow: hidden;
`;

const ImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  padding-right: 6px;
`;

const AppInfoWrapper = styled.div`
  max-width: 90%;
`;

const ContentsWrapper = styled.div`
  text-align: initial;
`;

const CommentDetailWrapper = styled.div`
  padding: 2% 3%;
`;

const ModalWrapper = styled.div`
  position: fixed;
  z-index: 2;
  left: 5%;
  top: 30%;
  width: 90%;
  height: 100%;
  overflow: auto;
`;

const ModalOverlay = styled.div`
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.75);
`;

type CommentsPageLayoutProps = {
  commentList: Comment[];
  application: {
    name: string;
    ownerName: string;
    iconSrc: string;
  };
  author: {
    iconSrc: string;
    name: string;
  };
  openModal: boolean;
  hasNextComment?: boolean;
  onPostComment(message: string, replyTo?: number): void;
  onDelete(id: number): void;
  getNextComment(): void;
  onOpenModal(): void;
  onCloseModal(): void;
};

export const CommentsPageLayout: React.FC<CommentsPageLayoutProps> = ({
  commentList,
  application,
  author,
  openModal,
  hasNextComment,
  onPostComment,
  onDelete,
  getNextComment,
  onOpenModal,
  onCloseModal,
}) => {
  const intl = useIntl();
  const [replyToComment, setReplyToComment] = useState<Comment | undefined>(
    undefined
  );

  const findComment = (id: number): Comment | undefined => {
    return commentList.find((comment) => {
      return comment.id === id;
    });
  };

  const getAuthorName = (comment: Comment): string => {
    if (comment.user) {
      return comment.user.name;
    } else if (comment.device.name) {
      return `[${comment.device.name}]`;
    } else {
      return intl.formatMessage({
        id: localeIds.commentDetail.unknownUserName,
      });
    }
  };

  return (
    <>
      <HeaderMenu>
        <HeaderMenuContents>
          <ImageWrapper>
            <Image src={application.iconSrc} />
          </ImageWrapper>
          <AppInfoWrapper>
            <Typography bold block ellipsis>
              {application.name}
            </Typography>
            <Typography
              color={Color.C3}
              block
              ellipsis
            >{`by ${application.ownerName}`}</Typography>
          </AppInfoWrapper>
        </HeaderMenuContents>
      </HeaderMenu>

      <ContentsWrapper>
        <InfiniteScroll
          dataLength={commentList.length}
          next={getNextComment}
          hasMore={hasNextComment || false}
          loader={<h4>Loading...</h4>}
        >
          {commentList.map(
            (comment): React.ReactNode => {
              return (
                <div key={`comment-${comment.id}`}>
                  <CommentDetailWrapper>
                    <CommentDetail
                      id={comment.id}
                      iconSrc={comment.user?.iconSrc || comment.device.iconSrc}
                      name={getAuthorName(comment)}
                      comment={comment.message}
                      createdAt={comment.createdDate}
                      allowDelete={comment.deletable}
                      reply={comment.reply}
                      onReply={(id: number) => {
                        setReplyToComment(findComment(id));
                        onOpenModal();
                      }}
                      onDelete={onDelete}
                    />
                  </CommentDetailWrapper>
                  <Driver />
                </div>
              );
            }
          )}
        </InfiniteScroll>
      </ContentsWrapper>

      {openModal ? (
        <>
          <ModalOverlay />
          <ModalWrapper>
            <CommentModal
              iconSrc={author.iconSrc}
              name={author.name}
              replyName={
                replyToComment ? getAuthorName(replyToComment) : undefined
              }
              onSubmit={(message) => {
                onPostComment(message, replyToComment?.id);
                onCloseModal();
                setReplyToComment(undefined);
              }}
              onCancel={() => {
                onCloseModal();
                setReplyToComment(undefined);
              }}
            />
          </ModalWrapper>
        </>
      ) : null}
    </>
  );
};
